import { BaseComponent } from '../lib/BaseComponent.js';

/**
 * Terms of Service Page Component
 * Displays the comprehensive terms of service
 */
export class TermsOfServicePage extends BaseComponent {
  protected async render(): Promise<void> {
    this.addStyles(this.getCommonStyles());
    this.addStyles(this.getStyles());

    const container = this.createElement('div', { className: 'legal-container' });

    // Header
    const header = this.createElement('div', { className: 'legal-header' });
    const backBtn = this.createElement('button', { className: 'back-btn' }, '← Back');
    backBtn.addEventListener('click', () => window.history.back());
    const title = this.createElement('h1', {}, 'Terms of Service');
    const lastUpdated = this.createElement('p', { className: 'last-updated' }, 'Last Updated: October 31, 2025');
    header.appendChild(backBtn);
    header.appendChild(title);
    header.appendChild(lastUpdated);
    container.appendChild(header);

    // Content
    const content = this.createElement('div', { className: 'legal-content' });
    content.innerHTML = `
      <section class="disclaimer">
        <h2>Agreement to Terms</h2>
        <p><strong>BY ACCESSING OR USING THE SERVICE, YOU AGREE TO BE BOUND BY THESE TERMS. IF YOU DO NOT AGREE TO THESE TERMS, DO NOT ACCESS OR USE THE SERVICE.</strong></p>
      </section>

      <section>
        <h2>1. Service Description</h2>
        <h3>1.1 Overview</h3>
        <p>FireGuard provides a cloud-based Software-as-a-Service (SaaS) platform for tracking fire extinguisher compliance in accordance with NFPA 10 and OSHA 1910.157 standards.</p>

        <h3>1.2 Key Features</h3>
        <p>The Service includes:</p>
        <ul>
          <li>Fire extinguisher inventory management</li>
          <li>QR code-based equipment tracking</li>
          <li>Monthly and annual inspection recording</li>
          <li>AI-powered photo analysis of inspection findings</li>
          <li>Compliance status tracking and reporting</li>
          <li>Location and facility management</li>
          <li>Alert notifications for overdue inspections</li>
          <li>Multi-user organization management</li>
          <li>Offline mobile support (PWA)</li>
        </ul>
      </section>

      <section>
        <h2>2. Account Registration and Access</h2>
        <h3>2.1 Eligibility</h3>
        <p>To use the Service, you must:</p>
        <ul>
          <li>Be at least 18 years old (or the age of majority in your jurisdiction)</li>
          <li>Have the legal authority to enter into binding contracts</li>
          <li>Not be prohibited from using the Service under applicable law</li>
          <li>Provide accurate and complete registration information</li>
        </ul>

        <h3>2.2 Account Security</h3>
        <p>You are responsible for:</p>
        <ul>
          <li>Maintaining the confidentiality of your login credentials</li>
          <li>All activities that occur under your account</li>
          <li>Notifying us immediately of any unauthorized access</li>
          <li>Using strong, unique passwords</li>
        </ul>
        <p><strong>We are not liable for losses caused by unauthorized use of your account.</strong></p>
      </section>

      <section>
        <h2>3. Acceptable Use Policy</h2>
        <h3>3.1 Permitted Use</h3>
        <p>You may use the Service only for lawful purposes in compliance with these Terms and applicable laws.</p>

        <h3>3.2 Prohibited Activities</h3>
        <p>You must not:</p>
        <ul>
          <li>Attempt to gain unauthorized access to the Service or systems</li>
          <li>Introduce viruses, malware, or harmful code</li>
          <li>Overload or disrupt the Service (DoS/DDoS attacks)</li>
          <li>Reverse engineer, decompile, or disassemble the Service</li>
          <li>Upload unlawful, harmful, or offensive content</li>
          <li>Resell, sublicense, or redistribute the Service</li>
          <li>Use the Service to develop competing products</li>
          <li>Submit fraudulent inspection records</li>
          <li>Misrepresent compliance status</li>
        </ul>
      </section>

      <section>
        <h2>4. Intellectual Property Rights</h2>
        <h3>4.1 Service Ownership</h3>
        <p>The Service, including all software, designs, text, graphics, logos, and other content, is owned by FireGuard or our licensors and is protected by United States and international intellectual property laws.</p>

        <h3>4.2 Your Data</h3>
        <p><strong>You retain all rights to the data and content you submit to the Service.</strong></p>
        <p>By submitting content, you grant us a limited license to use, store, process, and display your data solely to provide the Service.</p>

        <h3>4.3 License Grant</h3>
        <p>Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Service for your internal business purposes.</p>
      </section>

      <section>
        <h2>5. Privacy and Data Protection</h2>
        <p>Your use of the Service is also governed by our <a href="/privacy-policy">Privacy Policy</a>.</p>
        <p>We implement industry-standard security measures including:</p>
        <ul>
          <li>TLS 1.3 encryption for data in transit</li>
          <li>Encryption at rest for all stored data</li>
          <li>Access controls and authentication</li>
          <li>Regular security audits and vulnerability testing</li>
        </ul>
      </section>

      <section>
        <h2>6. Data Ownership and Backup</h2>
        <h3>6.1 Data Ownership</h3>
        <p><strong>You own your data.</strong> We do not claim ownership of any content you submit to the Service.</p>

        <h3>6.2 Data Storage and Backup</h3>
        <p>We store your data on Cloudflare's infrastructure with:</p>
        <ul>
          <li>Encrypted databases (Cloudflare D1)</li>
          <li>Object storage for photos (Cloudflare R2)</li>
          <li>Automatic backups retained for 30 days</li>
        </ul>
        <p><strong>You are responsible for maintaining independent backups of critical data.</strong></p>

        <h3>6.3 Data Export</h3>
        <p>You may export your data at any time using in-app export features or by contacting support.</p>
      </section>

      <section>
        <h2>7. Subscription and Payment</h2>
        <h3>7.1 Free Tier</h3>
        <p>The Service is currently offered free of charge during the beta period.</p>

        <h3>7.2 Future Pricing</h3>
        <p>When we implement paid subscriptions, we will provide 30 days' notice before charging. You may cancel before charges begin.</p>
      </section>

      <section class="highlight">
        <h2>8. Warranty Disclaimers</h2>
        <p><strong>THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND.</strong></p>
        <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, INCLUDING:</p>
        <ul>
          <li>Merchantability and fitness for a particular purpose</li>
          <li>Non-infringement of third-party rights</li>
          <li>Accuracy, completeness, or reliability of content</li>
          <li>Uninterrupted or error-free operation</li>
          <li>Complete protection against unauthorized access</li>
          <li>Guarantee of regulatory compliance</li>
        </ul>

        <h3>Compliance Responsibility</h3>
        <p><strong>YOU ARE SOLELY RESPONSIBLE FOR ENSURING YOUR COMPLIANCE WITH NFPA 10, OSHA 1910.157, AND OTHER APPLICABLE REGULATIONS.</strong></p>
        <p>The Service provides tools to assist with compliance tracking, but:</p>
        <ul>
          <li>We do not guarantee compliance with any regulations</li>
          <li>We do not provide legal or regulatory advice</li>
          <li>You must verify all compliance determinations independently</li>
          <li>You are responsible for inspection quality and accuracy</li>
        </ul>

        <h3>AI Analysis Limitations</h3>
        <p>AI-powered photo analysis is provided as an assistive tool:</p>
        <ul>
          <li>Results may be inaccurate or incomplete</li>
          <li>Human verification is required for all inspections</li>
          <li>We do not guarantee AI accuracy or reliability</li>
          <li>You are responsible for inspection validation</li>
        </ul>
      </section>

      <section class="highlight">
        <h2>9. Limitation of Liability</h2>
        <h3>9.1 Liability Cap</h3>
        <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS SHALL NOT EXCEED $100 USD.</p>

        <h3>9.2 Excluded Damages</h3>
        <p>WE SHALL NOT BE LIABLE FOR:</p>
        <ul>
          <li>Indirect damages (loss of profits, revenue, data, or business opportunities)</li>
          <li>Consequential damages from use or inability to use the Service</li>
          <li>Incidental damages (costs of substitute services)</li>
          <li>Special, exemplary, or punitive damages</li>
          <li>Third-party claims arising from your content or violations</li>
        </ul>

        <h3>9.3 Force Majeure</h3>
        <p>We are not liable for any failure or delay due to circumstances beyond our reasonable control, including natural disasters, telecommunications failures, government actions, cyberattacks, or third-party service provider failures.</p>
      </section>

      <section>
        <h2>10. Term and Termination</h2>
        <h3>10.1 Termination by You</h3>
        <p>You may terminate your account at any time by using the account deletion feature in Settings or emailing support@fireguard.example.com.</p>

        <h3>10.2 Termination by Us</h3>
        <p>We may terminate or suspend your access immediately for:</p>
        <ul>
          <li>Violation of these Terms or Acceptable Use Policy</li>
          <li>Fraudulent or illegal activities</li>
          <li>Non-payment of fees (when implemented)</li>
          <li>Risk to Service security or stability</li>
        </ul>

        <h3>10.3 Effect of Termination</h3>
        <p>Upon termination:</p>
        <ul>
          <li>Your right to access the Service immediately ceases</li>
          <li>We will delete your account and data per our Privacy Policy</li>
          <li>You may request a data export within 30 days</li>
          <li>After 30 days, data will be permanently deleted (except as required by law)</li>
        </ul>
      </section>

      <section>
        <h2>11. Compliance and Regulatory</h2>
        <h3>11.1 NFPA 10 and OSHA 1910.157</h3>
        <p>The Service is designed to assist with compliance tracking for:</p>
        <ul>
          <li><strong>NFPA 10</strong>: Standard for Portable Fire Extinguishers</li>
          <li><strong>OSHA 1910.157</strong>: Portable Fire Extinguishers</li>
        </ul>
        <p><strong>Compliance is your responsibility.</strong> The Service provides tools but does not guarantee regulatory compliance.</p>

        <h3>11.2 Inspection Standards</h3>
        <p>Monthly inspections should verify:</p>
        <ul>
          <li>Extinguisher accessibility and visibility</li>
          <li>Pressure gauge in operable range</li>
          <li>Tamper seal intact</li>
          <li>No visible damage or corrosion</li>
        </ul>
        <p>Annual maintenance requires certified technicians and is beyond the scope of this Service.</p>

        <h3>11.3 Recordkeeping</h3>
        <p>OSHA requires inspection records for 5-7 years. The Service retains records accordingly, but you are responsible for maintaining independent records.</p>
      </section>

      <section>
        <h2>12. General Provisions</h2>
        <h3>12.1 Governing Law</h3>
        <p>These Terms are governed by the laws of the United States, without regard to conflict of law principles.</p>

        <h3>12.2 Amendments</h3>
        <p>We may modify these Terms at any time by posting updated Terms on the Service and notifying you via email or in-app notification.</p>
        <p><strong>Continued use after changes constitutes acceptance.</strong></p>

        <h3>12.3 Entire Agreement</h3>
        <p>These Terms, together with our Privacy Policy, constitute the entire agreement between you and FireGuard regarding the Service.</p>
      </section>

      <section>
        <h2>13. Contact Information</h2>
        <p>For questions about these Terms:</p>
        <ul>
          <li><strong>Email</strong>: legal@fireguard.example.com</li>
          <li><strong>Support</strong>: support@fireguard.example.com</li>
        </ul>
      </section>

      <section class="disclaimer">
        <h2>Acknowledgment</h2>
        <p><strong>BY USING THE SERVICE, YOU ACKNOWLEDGE THAT:</strong></p>
        <ol>
          <li>You have read and understood these Terms</li>
          <li>You agree to be bound by these Terms</li>
          <li>You are authorized to accept these Terms on behalf of your organization</li>
          <li>You have reviewed our Privacy Policy</li>
          <li>You understand the warranty disclaimers and liability limitations</li>
        </ol>
        <p><strong>IF YOU DO NOT AGREE, DO NOT USE THE SERVICE.</strong></p>
      </section>
    `;

    container.appendChild(content);

    // Footer
    const footer = this.createElement('div', { className: 'legal-footer' });
    const footerText = this.createElement('p', {}, 'For questions or concerns, contact us at legal@fireguard.example.com');
    footer.appendChild(footerText);
    container.appendChild(footer);

    this.shadow.appendChild(container);
  }

  private getStyles(): string {
    return `
      .legal-container {
        max-width: 900px;
        margin: 0 auto;
        padding: var(--spacing-lg);
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      }

      .legal-header {
        margin-bottom: var(--spacing-xl);
        padding-bottom: var(--spacing-lg);
        border-bottom: 2px solid var(--gray-200);
      }

      .back-btn {
        background: none;
        border: none;
        color: var(--primary);
        font-size: 14px;
        cursor: pointer;
        padding: var(--spacing-sm);
        margin-bottom: var(--spacing-md);
        transition: opacity 0.2s;
      }

      .back-btn:hover {
        opacity: 0.7;
      }

      .legal-header h1 {
        font-size: 32px;
        font-weight: 700;
        color: var(--gray-900);
        margin: var(--spacing-md) 0;
      }

      .last-updated {
        font-size: 14px;
        color: var(--gray-600);
        margin: 0;
      }

      .legal-content {
        line-height: 1.7;
        color: var(--gray-800);
      }

      .legal-content section {
        margin-bottom: var(--spacing-xl);
      }

      .legal-content h2 {
        font-size: 24px;
        font-weight: 700;
        color: var(--gray-900);
        margin: var(--spacing-xl) 0 var(--spacing-md) 0;
        padding-top: var(--spacing-md);
      }

      .legal-content h3 {
        font-size: 18px;
        font-weight: 600;
        color: var(--gray-800);
        margin: var(--spacing-lg) 0 var(--spacing-sm) 0;
      }

      .legal-content p {
        margin: var(--spacing-md) 0;
        font-size: 15px;
      }

      .legal-content ul, .legal-content ol {
        margin: var(--spacing-md) 0;
        padding-left: var(--spacing-xl);
      }

      .legal-content li {
        margin: var(--spacing-sm) 0;
        font-size: 15px;
      }

      .legal-content a {
        color: var(--primary);
        text-decoration: underline;
      }

      .legal-content a:hover {
        text-decoration: none;
      }

      .legal-content strong {
        font-weight: 600;
        color: var(--gray-900);
      }

      .legal-content .highlight {
        background: var(--gray-50);
        border-left: 4px solid var(--warning);
        padding: var(--spacing-lg);
        margin: var(--spacing-xl) 0;
        border-radius: 4px;
      }

      .legal-content .disclaimer {
        background: var(--primary-light);
        padding: var(--spacing-lg);
        border-radius: 8px;
        border-left: 4px solid var(--primary);
        margin: var(--spacing-xl) 0;
      }

      .legal-content .disclaimer p, .legal-content .disclaimer li {
        font-weight: 600;
        color: var(--primary-dark);
      }

      .legal-footer {
        margin-top: var(--spacing-xl);
        padding-top: var(--spacing-lg);
        border-top: 2px solid var(--gray-200);
        text-align: center;
        color: var(--gray-600);
        font-size: 14px;
      }

      @media (max-width: 768px) {
        .legal-container {
          padding: var(--spacing-md);
        }

        .legal-header h1 {
          font-size: 24px;
        }

        .legal-content h2 {
          font-size: 20px;
        }

        .legal-content h3 {
          font-size: 16px;
        }

        .legal-content p, .legal-content li {
          font-size: 14px;
        }
      }
    `;
  }
}

// Register custom element
customElements.define('terms-of-service-page', TermsOfServicePage);
